// URL input. Drives `z.string().url()` per spec/tech-arch.md §7.
//
// **`type="url"` for native UX.** Browsers render the URL keyboard on
// mobile (with `.com` / `:/` keys) and surface URL autofill suggestions
// from the password manager.
//
// **No field-level URL regex.** URL-shape validation is the form wrapper's
// responsibility (Zod `.url()` runs on every change at `safeParse`). This
// component owns the markup + the controlled API only.
//
// **P-037.31 fourth post-Phase-Close §B — empty input → onChange(undefined).**
// When the input value is `''`, dispatch `undefined` instead of `''`. The
// form's `writeField` merge produces `{ ...obj, [name]: undefined }` which
// Zod's `.optional()` accepts identically to "key absent" — `z.string().url
// ().optional()` (e.g., `events-list` per-event `ctaUrl`) then validates
// cleanly. Without the coercion, `''` is a present string that fails
// `.url()` and surfaces "Invalid url" on an OPTIONAL field. Same boundary-
// coercion pattern as `<TextField>` (P-037.31 §B) + `<ImageUrlField>`
// Clear (P-037.31 §A).

import { FieldShell } from './field-shell'

interface UrlFieldProps {
  readonly id: string
  readonly name: string
  readonly label: string
  readonly helpText?: string
  readonly value: string
  // P-037.31 §B — `undefined` is the empty sentinel for optional fields
  // (was `''`). The form wrapper's writeField merges undefined as
  // "key present with undefined value", which Zod's `.optional()`
  // accepts identically to "key absent".
  readonly onChange: (next: string | undefined) => void
  readonly onBlur?: () => void
  readonly error?: string
  readonly required?: boolean
}

export function UrlField({
  id,
  name,
  label,
  helpText,
  value,
  onChange,
  onBlur,
  error,
  required = false,
}: UrlFieldProps) {
  return (
    <FieldShell
      id={id}
      label={label}
      required={required}
      {...(helpText !== undefined ? { helpText } : {})}
      {...(error !== undefined ? { error } : {})}
    >
      {(slot) => (
        <input
          {...slot.attrs}
          name={name}
          type="url"
          value={value}
          onInput={(e) => {
            // P-037.31 §B — coerce empty → undefined at the dispatch
            // boundary so optional-url schemas validate cleanly on
            // field clear. See file header for full rationale.
            const next = (e.currentTarget as HTMLInputElement).value
            onChange(next === '' ? undefined : next)
          }}
          onBlur={onBlur}
          class={slot.inputClass}
        />
      )}
    </FieldShell>
  )
}
